import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff, Fingerprint, ShieldCheck } from "lucide-react";
import { z } from "zod";
import { api, ApiError, clearCsrfToken, okSchema } from "@/lib/api";
import {
  passkeySupported,
  usePasskey,
  PasskeyError,
  type PublicKeyCredentialRequestOptionsJSON,
} from "@/lib/webauthn";
import { sessionQueryKey } from "@/lib/session";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Feedback";

/**
 * Shared shell for the two sign-in screens.
 *
 * A single dark canvas with the accent used exactly once, on the submit
 * button — the same restraint the rest of the product follows.
 */
function AuthLayout({
  eyebrow,
  title,
  description,
  children,
  footer,
}: {
  eyebrow: string;
  title: string;
  description: string;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <main className="bg-canvas grid min-h-dvh place-items-center px-5 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex items-center gap-2.5">
          <span
            aria-hidden
            className="bg-accent text-accent-content font-display grid size-9 place-items-center text-lg font-extrabold"
            style={{ borderRadius: "0.75rem 0.25rem 0.75rem 0.25rem" }}
          >
            M
          </span>
          <span className="font-display text-lg leading-none font-extrabold tracking-tight">
            mediora
          </span>
        </div>

        <p className="eyebrow mb-1.5">{eyebrow}</p>
        <h1 className="text-2xl">{title}</h1>
        <p className="text-content-muted mt-1.5 text-sm">{description}</p>

        <div className="mt-6">{children}</div>
        {footer && <div className="mt-5 text-sm">{footer}</div>}
      </div>
    </main>
  );
}

function PasswordInput({
  label,
  value,
  onChange,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="relative">
      <Input
        label={label}
        type={visible ? "text" : "password"}
        value={value}
        autoComplete={autoComplete}
        onChange={(event) => onChange(event.target.value)}
        className="pr-11"
        required
      />
      <button
        type="button"
        onClick={() => setVisible((current) => !current)}
        aria-label={visible ? "Sembunyikan password" : "Tampilkan password"}
        className="text-content-subtle hover:text-content absolute top-[30px] right-3"
      >
        {visible ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
      </button>
    </div>
  );
}

function useLoginSubmit(endpoint: string, fallback: string) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function submit(body: Record<string, string>) {
    setPending(true);
    setError(null);
    try {
      await api.post(endpoint, okSchema, body);
      // The token is bound to the session, which the server just replaced.
      clearCsrfToken();
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
      const from = (location.state as { from?: string } | null)?.from;
      navigate(from ?? fallback, { replace: true });
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : "Tidak dapat terhubung ke server.",
      );
    } finally {
      setPending(false);
    }
  }

  return { submit, error, pending };
}

export function AdminLogin() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const { submit, error, pending } = useLoginSubmit("/api/auth/admin/login", "/admin");

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    void submit({ username, password });
  }

  return (
    <AuthLayout
      eyebrow="Panel Admin"
      title="Masuk ke dashboard"
      description="Area khusus staf RS Mediora."
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && <Alert tone="danger">{error}</Alert>}
        <Input
          label="Username"
          value={username}
          autoComplete="username"
          onChange={(event) => setUsername(event.target.value)}
          required
        />
        <PasswordInput
          label="Password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
        />
        <Button type="submit" variant="primary" fullWidth loading={pending}>
          Masuk
        </Button>
      </form>

      <p className="text-content-subtle mt-5 flex items-start gap-2 text-xs">
        <ShieldCheck aria-hidden className="mt-px size-3.5 shrink-0" />
        Semua aktivitas admin tercatat pada audit trail.
      </p>
    </AuthLayout>
  );
}

const requestOptionsSchema = z.looseObject({ challenge: z.string() });

export function PatientLogin() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [passkeyError, setPasskeyError] = useState<string | null>(null);
  const [passkeyPending, setPasskeyPending] = useState(false);

  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { submit, error, pending } = useLoginSubmit(
    "/api/auth/patient/login",
    "/patient/dashboard",
  );

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    void submit({ identifier, password });
  }

  /**
   * Passkey sign-in, tanpa mengetik apa pun lebih dulu.
   *
   * Username tidak lagi wajib: bila kosong, server tidak mengirim daftar
   * kredensial dan authenticator menawarkan sendiri passkey yang dimilikinya
   * untuk domain ini. Pemiliknya dikenali dari kredensial yang dipilih.
   *
   * Bila pengguna kebetulan sudah mengisi kolom identitas, nilai itu tetap
   * dikirim supaya pilihan yang muncul menyempit ke satu akun.
   */
  async function signInWithPasskey() {
    setPasskeyError(null);
    setPasskeyPending(true);
    try {
      const options = await api.post<PublicKeyCredentialRequestOptionsJSON>(
        "/api/passkeys/authenticate/options",
        requestOptionsSchema as unknown as z.ZodType<PublicKeyCredentialRequestOptionsJSON>,
        identifier.trim() ? { username: identifier.trim() } : {},
      );
      const credential = await usePasskey(options);
      await api.post("/api/passkeys/authenticate/verify", okSchema, { credential });
      clearCsrfToken();
      await queryClient.invalidateQueries({ queryKey: sessionQueryKey });
      navigate("/patient/dashboard", { replace: true });
    } catch (cause) {
      setPasskeyError(
        cause instanceof PasskeyError || cause instanceof ApiError
          ? cause.message
          : "Masuk dengan passkey gagal.",
      );
    } finally {
      setPasskeyPending(false);
    }
  }

  return (
    <AuthLayout
      eyebrow="Portal Pasien"
      title="Masuk ke akun Anda"
      description="Gunakan username, nomor rekam medis, atau email terdaftar."
      footer={
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Link to="/forgot-password" className="text-content-muted hover:text-content underline underline-offset-2">
              Lupa password?
            </Link>
            <Link to="/patient/register" className="text-accent font-semibold hover:underline">
              Daftar akun
            </Link>
          </div>
          {/* Nothing else in the app linked to the admin login, so staff who
              landed here had no way to reach it except by typing the URL —
              and this form rejects their credentials, since it authenticates
              patients only. */}
          <p className="border-hairline text-content-subtle border-t pt-3 text-center text-xs">
            Staf RS Mediora?{" "}
            <Link to="/login" className="text-content-muted hover:text-content underline underline-offset-2">
              Masuk ke panel admin
            </Link>
          </p>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && <Alert tone="danger">{error}</Alert>}
        {passkeyError && <Alert tone="danger">{passkeyError}</Alert>}
        <Input
          label="Username / No. Rekam Medis / Email"
          value={identifier}
          autoComplete="username"
          onChange={(event) => setIdentifier(event.target.value)}
          required
        />
        <PasswordInput
          label="Password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
        />
        <Button type="submit" variant="primary" fullWidth loading={pending}>
          Masuk
        </Button>

        {/* Offered only where it can actually work: passkeys need a secure
            context, and a dead button is worse than no button. */}
        {passkeySupported() && (
          <>
            <div className="flex items-center gap-3">
              <span className="bg-hairline h-px flex-1" />
              <span className="text-content-subtle text-xs">atau</span>
              <span className="bg-hairline h-px flex-1" />
            </div>
            <Button
              type="button"
              fullWidth
              loading={passkeyPending}
              onClick={signInWithPasskey}
              icon={<Fingerprint className="size-4" />}
            >
              Masuk dengan passkey
            </Button>
          </>
        )}
      </form>
    </AuthLayout>
  );
}
